import type { Address } from '@montugroup/circuit-shared/types/address';

const TTL_MS_DEFAULT = 120 * 60 * 1000;
export const KEY = 'cnc_loc_query_session';

type Payload = { value: Address; expiresAt: number };
const now = () => Date.now();

const emit = () => window.dispatchEvent(new Event('cnc-location-query-changed'));

const readRaw = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(KEY);
};

const parse = (raw: string | null): Address | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Payload;
    if (!parsed?.expiresAt || parsed.expiresAt <= now()) {
      localStorage.removeItem(KEY);
      return null;
    }
    return parsed.value ?? null;
  } catch {
    localStorage.removeItem(KEY);
    return null;
  }
};

export const clickAndCollectLocationQueryStore = {
  getSnapshot: readRaw,

  getParsed(): Address | null {
    return parse(readRaw());
  },

  set(value: Address, ttlMs: number = TTL_MS_DEFAULT) {
    if (typeof window === 'undefined') {
      return;
    }
    const payload: Payload = { value, expiresAt: now() + ttlMs };
    localStorage.setItem(KEY, JSON.stringify(payload));
    emit();
  },

  clear() {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.removeItem(KEY);
    emit();
  },

  subscribe(onStoreChange: () => void) {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) {
        onStoreChange();
      }
    };
    const onLocal = () => onStoreChange();

    window.addEventListener('storage', onStorage);
    window.addEventListener('cnc-location-query-changed', onLocal);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('cnc-location-query-changed', onLocal);
    };
  },
};
